import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ExpandedState } from "@tanstack/react-table";
import { omitBy } from "@common/helpers/objects";
import {
    ServiceTemplateSchedulesSortValue,
    ServiceTemplateSchedulesView,
    ServiceTemplateSchedulesViewColumn,
    ServiceTemplateSchedulesViewSchema,
} from "@common/types/views/serviceTemplateSchedulesView";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { type DataGridHandle, ExpandAllButton } from "@design-system/DataGrid";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { mergeCachedView } from "@lib/mergeCachedView";
import { ServiceTemplateScheduleFilterableStatus } from "./serviceTemplateScheduleDisplayStatus";
import { serviceTemplateSchedulesViewConfig } from "./serviceTemplateSchedulesViewConfig";
import { ServiceTemplateScheduleStatusFilter } from "./ServiceTemplateScheduleStatusFilter";
import { hasActiveListFilters } from "./hasActiveListFilters";
import {
    DEFAULT_SCHEDULE_SORT,
    ServiceTemplateSchedulesTable,
} from "./list/ServiceTemplateSchedulesTable";

const AVAILABLE_FILTERS: FilterTabs[] = [
    FilterTabs.SCHEDULE_TEMPLATE,
    FilterTabs.SCHEDULE_PERIOD,
    FilterTabs.SCHEDULE_CLIENT,
    FilterTabs.ENGAGEMENT_TEAM,
];

const OPERATOR_FILTER_KEYS: readonly string[] = [
    "engagementTeamRoleOperator",
    "engagementTeamMemberOperator",
];

const defaultView: ServiceTemplateSchedulesView = {
    type: ViewType.SERVICE_TEMPLATE_SCHEDULES,
    columns: [
        {
            id: ServiceTemplateSchedulesViewColumn.TEMPLATE_NAME,
            options: { pin: "left" },
        },
        { id: ServiceTemplateSchedulesViewColumn.CLIENT },
        { id: ServiceTemplateSchedulesViewColumn.ENTITY },
        { id: ServiceTemplateSchedulesViewColumn.STATUS },
        { id: ServiceTemplateSchedulesViewColumn.SCHEDULE },
        { id: ServiceTemplateSchedulesViewColumn.NEXT_APPLICATION_DATE },
        { id: ServiceTemplateSchedulesViewColumn.NEXT_SERVICE_PERIOD },
        { id: ServiceTemplateSchedulesViewColumn.NEXT_WORK_END },
        { id: ServiceTemplateSchedulesViewColumn.ENGAGEMENT_TEAM },
        { id: ServiceTemplateSchedulesViewColumn.ESTIMATED_TIME },
        { id: ServiceTemplateSchedulesViewColumn.CREATED_AT },
        {
            id: ServiceTemplateSchedulesViewColumn.ACTIONS,
            options: { pin: "right" },
        },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.MD,
    },
};

export const ServiceTemplateSchedulesPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    // Present when arriving via the templates list's schedule-count cell —
    // scopes the Service filter to this one template so the drawer-less
    // "click count -> see this template's schedules" flow lands on a
    // pre-filtered Schedules tab instead of a dead-end drawer.
    const templateId = searchParams.get("templateId");

    // Tracks the last `templateId` the URL-sync effect (or our own param
    // removal) reconciled, so we can tell genuine back/forward navigation apart
    // from param changes we trigger ourselves and avoid clobbering a
    // just-applied filter. Seeded with the mount value, which the `view`
    // initializer below already reflects.
    const lastSyncedTemplateIdRef = useRef(templateId);

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<DataGridHandle>(null);

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<ServiceTemplateSchedulesView>(
            WORKSPACE_USER_CACHE_KEYS.SERVICE_TEMPLATE_SCHEDULES_PAGE_VIEW_CACHE_KEY,
            ServiceTemplateSchedulesViewSchema,
        );

    // Re-applies the Template column's `pin: "left"` override on top of any
    // stale cached view (e.g. from before PRO-10599), so it's always pinned
    // left even for users who cached a view before that became the default.
    // Also seeds the Service filter from the `templateId` URL param on first
    // render, so arriving via the schedule-count link lands pre-filtered
    // instead of requiring a follow-up effect (mirrors
    // OrganizationCloseTemplatesPage's `filters` initializer).
    const [view, setView] = useState<ServiceTemplateSchedulesView>(() => {
        const merged = mergeCachedView(
            cachedView,
            defaultView,
            serviceTemplateSchedulesViewConfig,
        );
        if (!templateId) {
            return merged;
        }
        return {
            ...merged,
            filters: { ...merged.filters, scheduleTemplateIds: [templateId] },
        };
    });

    const [search, setSearch] = useState<string | undefined>(undefined);

    const [sort, setSort] = useState<ServiceTemplateSchedulesSortValue>(
        DEFAULT_SCHEDULE_SORT,
    );

    // No status selected by default (i.e. show schedules of every status —
    // active, paused, archived, deleted). See PRO-10571.
    const [statusFilter, setStatusFilter] = useState<
        ServiceTemplateScheduleFilterableStatus[]
    >([]);

    const handleSearchChange = useCallback((value: string) => {
        // The search field emits "" when cleared; treat that as "no search" so
        // the backend (which rejects an empty search term) is never queried with
        // `search=`.
        setSearch(value.trim() ? value : undefined);
    }, []);

    // Tab filters (Period, Engagement team) live on view.filters so they're
    // persisted/restored alongside the rest of the cached view, mirroring how
    // Clients Work page stores its Filters-overlay state (see ClientsWorkPage).
    const tabFilters = view.filters;

    const clearTemplateIdParam = useCallback(() => {
        lastSyncedTemplateIdRef.current = null;
        setSearchParams(
            (params) => {
                params.delete("templateId");
                return params;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    const handleTabFiltersChange: FiltersChangeFn = useCallback(
        (partial) => {
            const nextFilters = omitBy(
                { ...view.filters, ...partial },
                (value) => value === null || value === undefined,
            );
            const next = { ...view, filters: nextFilters };
            setView(next);
            setCachedView(next);
            // Once the user edits the Service filter to anything other than the
            // exact URL scope (clears it, or picks a different selection), drop
            // the `templateId` param so a refresh or shared link reflects the
            // edited filter instead of re-seeding the stale scope. Pre-seed the
            // sync ref so the URL-sync effect below treats the resulting param
            // removal as our own write — not back/forward navigation — and
            // doesn't clobber the just-applied selection. Mirrors
            // OrganizationCloseTemplatesPage's equivalent templateId handling.
            if ("scheduleTemplateIds" in partial && templateId) {
                const nextScope = nextFilters.scheduleTemplateIds;
                const isExactUrlScope =
                    nextScope?.length === 1 && nextScope[0] === templateId;
                if (!isExactUrlScope) {
                    clearTemplateIdParam();
                }
            }
        },
        [view, setCachedView, clearTemplateIdParam, templateId],
    );

    // Reconciles the Service filter with the `templateId` URL scope whenever it
    // changes while the page stays mounted (back/forward navigation into,
    // between, or out of scoped URLs). The initial-mount case is handled by the
    // `view` initializer above. Compares the actual id — not just presence — so
    // A -> B re-seeds to B and removing the param clears the scope, rather than
    // leaving the previous template's schedules on screen. Guarded by the sync
    // ref so param removals we trigger from a user filter edit are skipped.
    // Mirrors OrganizationCloseTemplatesPage's templateId reconciliation.
    useEffect(() => {
        if (templateId === lastSyncedTemplateIdRef.current) {
            return;
        }
        lastSyncedTemplateIdRef.current = templateId;
        const nextFilters = templateId
            ? { ...view.filters, scheduleTemplateIds: [templateId] }
            : omitBy(
                  { ...view.filters, scheduleTemplateIds: undefined },
                  (value) => value === null || value === undefined,
              );
        const next = { ...view, filters: nextFilters };
        setView(next);
        setCachedView(next);
    }, [templateId, view, setCachedView]);

    const query = useMemo(
        () => ({
            search,
            templateIds: tabFilters.scheduleTemplateIds ?? undefined,
            periods: tabFilters.schedulePeriods ?? undefined,
            clientIds: tabFilters.scheduleClientIds ?? undefined,
            engagementTeamRoles: tabFilters.engagementTeamRoles ?? undefined,
            engagementTeamRoleOperator: tabFilters.engagementTeamRoleOperator,
            engagementTeamMemberIds:
                tabFilters.engagementTeamMemberIds ?? undefined,
            engagementTeamMemberOperator:
                tabFilters.engagementTeamMemberOperator,
        }),
        [search, tabFilters],
    );

    // Drops every filter (search, status, tab filters incl. the templateId URL
    // scope). Pre-seeds the sync ref so the URL-sync effect treats the param
    // removal as our own write.
    const handleClearFilters = useCallback(() => {
        setSearch(undefined);
        setStatusFilter([]);
        const next = { ...view, filters: {} };
        setView(next);
        setCachedView(next);
        if (templateId) {
            clearTemplateIdParam();
        }
    }, [view, setCachedView, clearTemplateIdParam, templateId]);

    const hasActiveFilters =
        search !== undefined ||
        statusFilter.length > 0 ||
        hasActiveListFilters(
            omitBy(tabFilters, (_value, key) =>
                OPERATOR_FILTER_KEYS.includes(key),
            ),
        );

    const handleViewChange = useCallback(
        (newView: View) => {
            const parsed =
                ServiceTemplateSchedulesViewSchema.safeParse(newView);
            if (parsed.success) {
                setView(parsed.data);
                setCachedView(parsed.data);
            }
        },
        [setCachedView],
    );

    return (
        <>
            <TablePageHeader
                isFullPage
                search={search}
                onSearchChange={handleSearchChange}
                filters={tabFilters}
                onFiltersChange={handleTabFiltersChange}
                availableFilters={AVAILABLE_FILTERS}
            >
                <ServiceTemplateScheduleStatusFilter
                    selectedStatuses={statusFilter}
                    onChange={setStatusFilter}
                />
                <ExpandAllButton expanded={expanded} handleRef={tableRef} />
                <ColumnSettings
                    view={view}
                    viewConfig={serviceTemplateSchedulesViewConfig}
                    defaultView={defaultView}
                    onViewChange={handleViewChange}
                />
            </TablePageHeader>

            <ServiceTemplateSchedulesTable
                ref={tableRef}
                view={view}
                defaultView={defaultView}
                onViewChange={handleViewChange}
                query={query}
                sort={sort}
                onSortChange={setSort}
                statusFilter={statusFilter}
                expanded={expanded}
                onExpandedChange={setExpanded}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
            />
        </>
    );
};
